import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {
    registerTemplateGraphNode,
    NumberExport,
    numberLike,
    isNumberLike,
    stringLike,
    skipped,
    getTemplateNodeReturns,
    EMPTY_LIST,
    EMPTY_OBJECT,
    TemplateNode,
    TypeDescriptors,
    visitNone,
} from "@cm/template-nodes"
import type {
    NumberLike,
    StringLike,
    OnFilterActiveContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    ObjectPath,
    OnCompileContext,
    BuilderInlet,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    InterfaceDescriptor,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {mattressComponentGraphLikeMarker, isMattressCurveLikeNode, isMattressReferenceFrameLikeNode} from "#mattress/role-markers"
import {mattressComponentGraphLike, mattressCurveLike, mattressReferenceFrameLike} from "#mattress/validation"
import type {MattressComponentGraphLikeNode, MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import {
    mattressComponentInstanceParametersParameterValue,
    MattressComponentInstance,
    outputAndExportInfoForOutputInterfaceDescriptor,
} from "#mattress/nodes/component/mattress-component-instance"
import type {
    MattressComponentInstanceParametersParameterValue,
    MattressComponentInstanceParametersParameters,
} from "#mattress/nodes/component/mattress-component-instance"
import {MattressCurveExport} from "#mattress/nodes/curve/mattress-curve-export"
import {MattressReferenceFrameExport} from "#mattress/nodes/reference-frame/mattress-reference-frame-export"
import type {
    MattressComponentGraphData,
    MattressComponentGraphGeneratorInfoData,
    MattressMeshData,
    MattressNodePath,
} from "#mattress/nodes/component/mattress-component-types"
import {MattressExport} from "#mattress/nodes/mattress-export"
import {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import {MattressNodes} from "#mattress/nodes/mattress-nodes"
import type {MattressComponentGraphNode} from "#mattress/nodes/mattress-nodes"
import {CompileRunMattressComponent} from "#mattress/runtime-graph/nodes/compile-run-mattress-component"
import type {MattressComponentInterfaceDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {
    MattressTemplateInterfaceDescriptorTypeDescriptor,
    MattressComponentInterfaceDescriptorTypeDescriptor,
} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {filterAndWrapInterface, getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"

const TD = TypeDescriptors

const mattressTemplateInterfaceDescriptorArrayDesc = TD.Array(MattressTemplateInterfaceDescriptorTypeDescriptor)
const mattressComponentInterfaceDescriptorArrayDesc = TD.Array(MattressComponentInterfaceDescriptorTypeDescriptor)
const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

export type MattressComponentGraphGeneratorUnitRepeat1DParameters = {
    type: "MattressComponentGraphGeneratorUnitRepeat1D"
    baseGraph: MattressComponentGraphLikeNode | null
    unitGraph: MattressComponentGraphLikeNode | null
    unitInputs: Record<string, MattressComponentInstanceParametersParameterValue | MattressExport | NumberExport | NumberLike>
    unitCount: NumberLike | null
    unitIdPrefix: StringLike | null
    outputsIfUnitCountZero?: Record<string, NumberLike | MattressCurveLikeNode | MattressReferenceFrameLikeNode>
}

export type MattressComponentGraphGeneratorParameters = {
    name: string
    parameters: MattressComponentGraphGeneratorUnitRepeat1DParameters
}

const unitRepeat1DSchema = z.object({
    type: z.literal("MattressComponentGraphGeneratorUnitRepeat1D"),
    baseGraph: z.nullable(mattressComponentGraphLike),
    unitGraph: z.nullable(mattressComponentGraphLike),
    unitInputs: z.record(
        z.string(),
        z.union([
            mattressComponentInstanceParametersParameterValue,
            graphNode(MattressCurveExport),
            graphNode(MattressReferenceFrameExport),
            graphNode(NumberExport),
            numberLike,
        ]),
    ),
    unitCount: z.nullable(numberLike),
    unitIdPrefix: z.nullable(stringLike),
    outputsIfUnitCountZero: z.optional(z.record(z.string(), z.union([numberLike, mattressCurveLike, mattressReferenceFrameLike]))),
})

const definition: TemplateNodeDefinition<MattressComponentGraphGeneratorParameters> = {
    nodeClass: "MattressComponentGraphGenerator",
    validation: {
        parameters: z.object({
            name: z.string(),
            parameters: unitRepeat1DSchema,
        }),
        returns: getTemplateNodeReturns(),
    },
}

//////////////////////////////////////////////////////////////////////////////////////////////
// Generators for the unit-repeat-1D scheme: expand a base graph + N copies of a unit graph,
// chaining each unit's outputs into the next unit's inputs and exposing the last unit's outputs.

function generateMattressComponentGraphUnitRepeat1D(
    baseGraphNodes: MattressComponentGraphNode[],
    unitGraph: MattressComponentGraph,
    unitInputs: {[inputId: string]: MattressComponentInstanceParametersParameterValue | MattressExport | NumberExport},
    unitCount: number,
    unitIdPrefix: string,
    unitDescriptorList: (InterfaceDescriptor<unknown> | MattressComponentInterfaceDescriptor<unknown>)[],
    outputsIfUnitCountZero?: Record<string, NumberLike | MattressCurveLikeNode | MattressReferenceFrameLikeNode>,
): MattressComponentGraphNode[] {
    const generatedNodes: MattressComponentGraphNode[] = [...baseGraphNodes]

    const inputsIds = Object.keys(unitInputs)
    const generatedUnitInstances: MattressComponentInstance[] = []
    for (let unitIdx = 0; unitIdx < unitCount; unitIdx++) {
        const unitId = `${unitIdPrefix}-${unitIdx}`

        const thisUnitInputs: MattressComponentInstanceParametersParameters = {}
        inputsIds.forEach((inputId) => {
            const parameterValue = unitInputs[inputId]
            if (!(parameterValue instanceof TemplateNode)) {
                thisUnitInputs[inputId] = parameterValue
                return
            }

            if (parameterValue instanceof MattressExport || parameterValue instanceof NumberExport) {
                if (unitIdx !== 0) {
                    const priorUnitInstanceOutput = generatedUnitInstances[unitIdx - 1].createOutputForExport(parameterValue)
                    thisUnitInputs[inputId] = priorUnitInstanceOutput
                    generatedNodes.push(priorUnitInstanceOutput)
                }
            } else if (baseGraphNodes.includes(parameterValue as MattressComponentGraphNode)) {
                thisUnitInputs[inputId] = parameterValue as MattressComponentInstanceParametersParameterValue
            }
        })

        const unitInstance = new MattressComponentInstance({
            parameters: {
                name: unitId,
                id: unitId,
                graph: unitGraph,
                parameters: thisUnitInputs,
            },
        })

        generatedNodes.push(unitInstance)
        generatedUnitInstances.push(unitInstance)
    }

    for (const unitDescriptor of unitDescriptorList) {
        if (unitDescriptor.props.type === "input") continue
        const exportAndOutputInfo = outputAndExportInfoForOutputInterfaceDescriptor(unitDescriptor)

        const exportValueNode = (() => {
            if (unitCount === 0) return outputsIfUnitCountZero?.[unitDescriptor.props.id] ?? null

            const lastUnitInstance = generatedUnitInstances[generatedUnitInstances.length - 1]
            const outputNode = lastUnitInstance.createOutput(unitDescriptor.props.id, exportAndOutputInfo)
            generatedNodes.push(outputNode)
            return outputNode
        })()

        if (unitCount !== 0 && exportValueNode === null)
            throw new Error(`Failed to create output node for ${unitDescriptor.props.id}, for unit count ${unitCount}`)
        else if (exportValueNode === null) continue

        if (exportAndOutputInfo.source === "template") {
            if (exportAndOutputInfo.type === "number" && isNumberLike(exportValueNode)) {
                const exportNode = new exportAndOutputInfo.exportNodeClass({
                    parameters: {
                        name: exportAndOutputInfo.name,
                        id: unitDescriptor.props.id,
                        node: exportValueNode,
                    },
                })
                generatedNodes.push(exportNode)
            }
        } else if (exportAndOutputInfo.source === "component" && exportValueNode instanceof TemplateNode) {
            if (exportAndOutputInfo.type === "mattressCurve" && isMattressCurveLikeNode(exportValueNode)) {
                const exportNode = new exportAndOutputInfo.exportNodeClass({
                    parameters: {
                        name: exportAndOutputInfo.name,
                        id: unitDescriptor.props.id,
                        node: exportValueNode,
                    },
                })
                generatedNodes.push(exportNode)
            } else if (exportAndOutputInfo.type === "mattressReferenceFrame" && isMattressReferenceFrameLikeNode(exportValueNode)) {
                const exportNode = new exportAndOutputInfo.exportNodeClass({
                    parameters: {
                        name: exportAndOutputInfo.name,
                        id: unitDescriptor.props.id,
                        node: exportValueNode,
                    },
                })
                generatedNodes.push(exportNode)
            }
        } else throw new Error(`Unsupported export source ${exportAndOutputInfo.source} for output ${unitDescriptor.props.id} of unit graph`)
    }

    return generatedNodes
}

function evaluateDescriptorsForUnit(
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    partialMattressMeshData: BuilderInlet<MattressMeshData>,
    unitGraphData: BuilderInlet<MattressComponentGraphData>,
    objectPath: BuilderInlet<ObjectPath>,
) {
    const {templateContext} = evaluator

    const {templateDescriptorList, componentDescriptorList} = scope.node(CompileRunMattressComponent, {
        graphData: unitGraphData,
        partialMattressMeshData,
        templateContext,
        templateInputs: {},
        componentInputs: {},
        nodePath: EMPTY_LIST,
        objectPath,
        componentDepth: 1,
        exposeClaimedSubComponentInputs: true,
        forwardedLocalInstanceTemplateOutputs: EMPTY_OBJECT,
        forwardedLocalInstanceDescriptorList: EMPTY_LIST,
    })

    const [templateDescriptorListValid] = scope.branch(templateDescriptorList)
    const [componentDescriptorListValid] = scope.branch(componentDescriptorList)

    const wrappedTemplateDescriptorList = scope.pureLambda(
        templateDescriptorListValid,
        (templateDescriptorList) => filterAndWrapInterface(templateDescriptorList, undefined, EMPTY_LIST, true),
        "wrapTemplateDescriptorListForUnit",
        {
            input: mattressTemplateInterfaceDescriptorArrayDesc,
            output: mattressTemplateInterfaceDescriptorArrayDesc,
        },
    )

    const wrappedComponentDescriptorList = scope.pureLambda(
        componentDescriptorListValid,
        (componentDescriptorList) => filterAndWrapInterface(componentDescriptorList, undefined, EMPTY_LIST, true),
        "wrapComponentDescriptorListForUnit",
        {
            input: mattressComponentInterfaceDescriptorArrayDesc,
            output: mattressComponentInterfaceDescriptorArrayDesc,
        },
    )

    return {
        templateDescriptorList: wrappedTemplateDescriptorList,
        componentDescriptorList: wrappedComponentDescriptorList,
    }
}

@registerTemplateGraphNode
export class MattressComponentGraphGenerator extends MattressNode<MattressComponentGraphGeneratorParameters> implements MattressComponentGraphLikeNode {
    get [mattressComponentGraphLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressComponentGraphGeneratorParameters> {
        const type = this.parameters.parameters.type
        if (type === "MattressComponentGraphGeneratorUnitRepeat1D") {
            const {unitGraph, unitCount, unitIdPrefix} = this.parameters.parameters
            if (unitGraph === null || unitCount === null || unitIdPrefix === null) return skipped
            return visitNone(this.parameters)
        }
        return skipped
    }

    public override onCompile(
        context: OnCompileContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressComponentGraphGeneratorParameters> {
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {templateScope} = evaluator
        const {evaluatePartialMattressMeshData, evaluatedData, nodePath, objectPath} = mattressContext
        const {infoDataLists} = evaluatedData
        const {type} = this.parameters.parameters

        const scope = evaluator.getScope(this)

        const newNodePath = scope.pureLambda(scope.tuple(nodePath, this), ([nodePath, instance]) => [...nodePath, instance], "newPath", {
            output: mattressNodePathArrayDesc,
        })
        const newObjectPath = scope.pureLambda(scope.tuple(objectPath, this), ([objectPath, instance]) => [...objectPath, instance], "newObjectPath")

        let generatedGraphNodes: BuilderOutlet<MattressComponentGraphNode[]> | null = null
        if (type === "MattressComponentGraphGeneratorUnitRepeat1D") {
            const {
                baseGraph: parameterBaseGraph,
                unitGraph: parameterUnitGraph,
                unitInputs,
                unitCount,
                unitIdPrefix: parameterUnitIdPrefix,
                outputsIfUnitCountZero,
            } = this.parameters.parameters
            if (parameterUnitGraph === null || unitCount === null || parameterUnitIdPrefix === null) return visitNone(this.parameters)

            const [baseGraphDataValid] = scope.branch(parameterBaseGraph ? evaluator.evaluate(parameterBaseGraph) : null)
            const baseGraphNodes = scope.phi(
                scope.pureLambda(baseGraphDataValid, ({graph}) => [...graph.parameters.nodes.parameters.list], "getBaseGraphNodes"),
                EMPTY_LIST,
            )
            const [unitGraphDataValid] = scope.branch(evaluator.evaluate(parameterUnitGraph))
            const [unitCountValid] = scope.branch(evaluator.evaluate(unitCount))
            const [unitIdPrefixValid] = scope.branch(evaluator.evaluate(parameterUnitIdPrefix))

            const [partialMattressMeshDataValid] = scope.branch(evaluatePartialMattressMeshData(scope, evaluator))

            const {templateDescriptorList: unitTemplateDescriptorList, componentDescriptorList: unitComponentDescriptorList} = evaluateDescriptorsForUnit(
                scope,
                evaluator,
                partialMattressMeshDataValid,
                unitGraphDataValid,
                newObjectPath,
            )

            generatedGraphNodes = scope.pureLambda(
                scope.tuple(
                    baseGraphNodes,
                    scope.get(unitGraphDataValid, "graph"),
                    unitInputs,
                    unitCountValid,
                    unitIdPrefixValid,
                    unitTemplateDescriptorList,
                    unitComponentDescriptorList,
                    outputsIfUnitCountZero,
                ),
                ([
                    baseGraphNodes,
                    unitGraph,
                    unitInputs,
                    unitCount,
                    unitIdPrefix,
                    unitTemplateDescriptorList,
                    unitComponentDescriptorList,
                    outputsIfUnitCountZero,
                ]) =>
                    generateMattressComponentGraphUnitRepeat1D(
                        baseGraphNodes,
                        unitGraph,
                        unitInputs,
                        unitCount,
                        unitIdPrefix,
                        [...unitTemplateDescriptorList, ...unitComponentDescriptorList],
                        outputsIfUnitCountZero,
                    ),
                "generateGraphNodesForUnitRepeat1D",
            )

            const infoData = scope.struct<MattressComponentGraphGeneratorInfoData>("mattressComponentGraphGeneratorInfoData", {
                type: "mattressComponentGraphGenerator",
                templateDescriptorList: unitTemplateDescriptorList,
                componentDescriptorList: unitComponentDescriptorList,
                path: newNodePath,
            })
            infoDataLists.push(scope.tuple(infoData))
        }
        if (!generatedGraphNodes) return skipped

        const generatedGraphSrc = scope.const(
            () =>
                new MattressComponentGraph({
                    parameters: {
                        name: `${this.parameters.name}-generatedGraph`,
                        nodes: new MattressNodes({parameters: {list: []}}),
                    },
                }),
            "gneratedGraphSrc",
        )

        const generatedGraphData = scope.pureLambda(
            scope.tuple(generatedGraphSrc, generatedGraphNodes),
            ([generatedGraphSrc, generatedGraphNodes]) => {
                generatedGraphSrc.parameters.nodes.clear()
                generatedGraphNodes.forEach((generatedGraphNode) => generatedGraphSrc.parameters.nodes.addEntry(generatedGraphNode))
                generatedGraphSrc.parameters.nodes.parents.clear()

                const graph = generatedGraphSrc.clone({
                    cloneInstanceIds: true,
                    cloneSubNode: () => false,
                })

                return {graph, contentHash: graph.getHash()}
            },
            "generateGraphData",
        )

        templateScope.alias(scope.phi(generatedGraphData, null), `mattressComponentGraphData-${evaluator.getLocalId(this)}`)
        return visitNone(this.parameters)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MattressComponentGraphData | null> {
        return scope.unresolvedToNull(
            evaluator.templateScope.resolve<MattressComponentGraphData | null>(`mattressComponentGraphData-${evaluator.getLocalId(this)}`),
        )
    }
}
